import React from 'react';
import { Container, Typography, Box, Paper, useMediaQuery, useTheme } from '@mui/material';
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot } from '@mui/lab';
import { motion } from 'framer-motion';
import { Work as WorkIcon } from '@mui/icons-material';

const Experience = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const experiences = [
    {
      title: 'Software Developement Engineer I',
      company: 'MiniOrange Security Software Pvt. Ltd.',
      period: 'June 2025 – Present',
        description: [
            'Developed and enhanced security features for an enterprise Identity and Access Management (IAM) platform',
            'Implemented authentication and authorization workflows using Spring Security, OAuth 2.0, SAML, JWT, and Kerberos',
            'Worked on backend services using Java, Vert.x, PostgreSQL, and Redis in a distributed system architecture',
            'Optimized application performance through code refactoring, database query improvements, and connection management',
            'Gained hands-on experience with Docker, RabbitMQ, Redis, Nginx, and modern enterprise Java application architecture'
        ]
    },
    {
      title: 'Software Engineer Intern',
      company: 'MiniOrange Security Software Pvt. Ltd.',
      period: 'Jan 2025 – June 2025',
      description: [
        'Learned about Kerberos authentication, Single Sign-On (SSO) flows SAML, OAuth, JWT, Nginx',
        'Worked extensively on Spring Security, Shiro Security, and cache management',
        'Developed JUnit test cases using Mockito and Assertions'
      ]
    },
    {
      title: 'Data Science Intern',
      company: 'Amcergrade Pvt. Ltd.',
      period: 'Jan 2023 – Apr 2023',
      description: [
        'Worked on predictive analytics and predicting sales trends',
        'Preprocessed and cleaned data to improve forecasting accuracy',
        'Applied and evaluated multiple ML algorithms using various metrics'
      ]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  const getExpColor = () => {
    return 'var(--primary)';
  };

  return (
    <Box 
      component="section"
      sx={{ 
        py: { xs: 6, md: 12 },
        minHeight: { xs: 'auto', md: '100vh' },
        display: 'flex',
        alignItems: 'center',
        background: 'transparent',
        borderRadius: 2,
        boxShadow: 'none'
      }}
    >
      <Container>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <motion.div variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
          }}>
            <Typography 
              variant="h3" 
              gutterBottom 
              align="center" 
              sx={{ 
                mb: 6,
                fontFamily: "'Chivo Mono', monospace",
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                background: 'linear-gradient(135deg, #ffffff, rgba(255, 255, 255, 0.7))',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                filter: 'drop-shadow(0 0 10px rgba(255, 255, 255, 0.1))',
                opacity: 1
              }}
            >
              Professional Experience
            </Typography>
          </motion.div>

          <Timeline position={isMobile ? "right" : "alternate"}>
            {experiences.map((experience, experienceIndex) => (
              <TimelineItem key={experienceIndex} sx={{ '&:before': { display: isMobile ? 'none' : 'block' } }}>
                <TimelineSeparator>
                  <motion.div
                    variants={itemVariants}
                    whileHover={{ scale: 1.2, rotate: 10 }}
                  >
                    <TimelineDot 
                      sx={{ 
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                        boxShadow: '0 0 15px rgba(255, 255, 255, 0.1)',
                        p: 1.5,
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <WorkIcon />
                    </TimelineDot>
                  </motion.div>
                  <TimelineConnector 
                    component={motion.div}
                    initial={{ height: 0 }}
                    whileInView={{ height: 'auto' }}
                    transition={{ duration: 1.5, ease: "easeInOut" }}
                    sx={{ 
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      width: '2px'
                    }} 
                  />
                </TimelineSeparator>
                <TimelineContent>
                  <motion.div
                    variants={{
                      hidden: { 
                        opacity: 0, 
                        x: isMobile ? 50 : (experienceIndex % 2 === 0 ? -100 : 100) 
                      },
                      visible: { 
                        opacity: 1, 
                        x: 0,
                        transition: { duration: 0.7, ease: "easeOut" }
                      }
                    }}
                  >
                    <Paper
                      className="glass-card"
                      sx={{ 
                        p: { xs: 2, sm: 3, md: 4 },
                        bgcolor: 'transparent',
                        border: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                          transform: 'none',
                          boxShadow: 'none',
                          borderColor: `${getExpColor()}66`
                        }
                      }}
                    >
                      <Typography 
                        variant="h5" 
                        component="h2" 
                        sx={{ 
                          color: getExpColor(),
                          fontWeight: 700,
                          fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.8rem' },
                          mb: 1,
                          textAlign: 'center',
                          opacity: 1
                        }}
                      >
                        {experience.title}
                      </Typography>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          color: '#ffffff',
                          mb: 1.5,
                          fontWeight: 600,
                          fontSize: { xs: '1.15rem', sm: '1.25rem', md: '1.45rem' },
                          textAlign: 'center',
                          opacity: 1
                        }}
                      >
                        {experience.company}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'var(--text-dim)',
                          mb: 4,
                          fontSize: '0.95rem',
                          fontStyle: 'italic',
                          textAlign: 'right',
                          display: 'block',
                          opacity: 0.8
                        }}
                      >
                        {experience.period}
                      </Typography>
                      <Box 
                        component="ul" 
                        sx={{ 
                          mt: 2, 
                          pl: 2,
                          '& li': {
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateX(8px)'
                            }
                          }
                        }}
                      >
                        {experience.description.map((descriptionPoint, descriptionIndex) => (
                          <Typography 
                            component="li" 
                            key={descriptionIndex} 
                            variant="body1" 
                            sx={{ 
                              mb: 2,
                              color: '#ffffff',
                              fontSize: { xs: '1rem', md: '1.1rem' },
                              lineHeight: 1.7,
                              textAlign: 'left',
                              opacity: 0.9,
                              '&::marker': {
                                color: getExpColor()
                              }
                            }}
                          >
                            {descriptionPoint}
                          </Typography>
                        ))}
                      </Box>
                    </Paper>
                  </motion.div>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Experience; 